import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from "@mui/material";
import {
  SportsTennis as TennisIcon,
  Person as PersonIcon,
  TableRestaurant as TableIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import firestoreInstance from "../../services/firestoreInstance";
import { Matchs } from "../../types/Matchs";
import { Players } from "../../types/Players";
import { useAppSelector } from "../../store";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import {
  applyTournamentDossard,
  fetchTournamentDossards,
} from "../../helpers/fetchParticipationsWithPlayer";

// Étendre le type Matchs pour inclure les nouvelles propriétés
interface ExtendedMatchs extends Matchs {
  tableauName?: string;
  tableauLabel?: string;
  tourLabel?: string;
}

const MatchDetail: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const tournoi = useAppSelector(getTournoi);
  const [match, setMatch] = useState<ExtendedMatchs | null>(null);
  const [players, setPlayers] = useState<Players[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMatchData = async () => {
      if (!matchId) return;
      const tournamentId = tournoi?.id;
      if (!tournamentId) {
        setLoading(true);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const matchDoc = await getDoc(
          doc(firestoreInstance, "tournois", tournamentId, "matchs", matchId)
        );

        if (!matchDoc.exists()) {
          setError("Match non trouvé");
          return;
        }

        const matchData = {
          id: matchDoc.id,
          ...matchDoc.data(),
        } as ExtendedMatchs;
        setMatch(matchData);

        // Récupérer les données des joueurs + dossards du tournoi
        if (matchData.matchsPlayerParticipations) {
          const dossardsMap = await fetchTournamentDossards(tournamentId);
          const playerPromises = matchData.matchsPlayerParticipations.map(
            async (mpp) => {
              const playerDoc = await getDoc(
                doc(firestoreInstance, "players", mpp.playersId)
              );
              if (playerDoc.exists()) {
                const raw = {
                  id: playerDoc.id,
                  ...playerDoc.data(),
                } as Players;
                const { dossard: _legacy, ...withoutLegacy } = raw as Players & {
                  dossard?: number;
                };
                return applyTournamentDossard(
                  withoutLegacy as Players,
                  dossardsMap
                );
              }
              return null;
            }
          );

          const playersData = await Promise.all(playerPromises);
          setPlayers(playersData.filter((p): p is Players => p !== null));
        }

        // Récupérer les données du tableau et du tour
        if (matchData.tableauId) {
          try {
            const boardDoc = await getDoc(
              doc(
                firestoreInstance,
                "tournois",
                tournamentId,
                "tableaux",
                matchData.tableauId
              )
            );

            if (boardDoc.exists()) {
              const boardData = boardDoc.data();
              const tourData = matchData.tour;

              setMatch({
                ...matchData,
                tableauName: boardData.name,
                tableauLabel: boardData.label,
                tourLabel: tourData?.label,
              });
            }
          } catch (err) {
            console.error("Erreur lors de la récupération du tableau:", err);
          }
        }
      } catch (err) {
        console.error("Erreur lors de la récupération du match:", err);
        setError("Erreur lors de la récupération du match");
      } finally {
        setLoading(false);
      }
    };

    fetchMatchData();
  }, [matchId, tournoi?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "CREE":
        return "primary";
      case "ENCOURS":
        return "success";
      case "TERMINE":
        return "default";
      case "DOUBLON":
        return "warning";
      case "LIBERE":
        return "info";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "CREE":
        return "Créé";
      case "ENCOURS":
        return "En cours";
      case "TERMINE":
        return "Terminé";
      case "DOUBLON":
        return "En doublon";
      case "LIBERE":
        return "Libéré";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!match) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Match non trouvé</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Paper sx={{ p: 3 }}>
        {/* En-tête */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <TennisIcon sx={{ mr: 2, color: "primary.main", fontSize: 40 }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              Détails du match
            </Typography>
          </Box>
        </Box>

        {/* Statut du match */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Statut du match
          </Typography>
          <Chip
            label={getStatusLabel(match.status || "INCONNU")}
            color={getStatusColor(match.status || "INCONNU")}
            size="medium"
            icon={<TrophyIcon />}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Informations du tableau */}
        {match.tableauId && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Tableau
            </Typography>
            <Typography variant="body1">
              {match.tableauName && match.tableauLabel
                ? `${match.tableauName} - ${match.tableauLabel}`
                : match.tableauLabel || match.tableauName || "Tableau inconnu"}
            </Typography>
            {match.tourLabel && (
              <Typography variant="body2" color="text.secondary">
                Tour: {match.tourLabel}
              </Typography>
            )}
          </Box>
        )}

        {/* Table assignée */}
        {match.tableId && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Table assignée
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TableIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="body1">Table {match.tableId}</Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Joueurs */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Joueurs ({players.length})
          </Typography>
          <List>
            {players.map((player, index) => (
              <ListItem key={player.id} sx={{ pl: 0 }}>
                <ListItemAvatar>
                  <Avatar>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${player.name} ${player.surname}`}
                  secondary={
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Licence: {player.licenceId}
                      </Typography>
                      {player.club && (
                        <Typography variant="body2" color="text.secondary">
                          Club: {player.club}
                        </Typography>
                      )}
                      {player.dossard && (
                        <Typography variant="body2" color="text.secondary">
                          Dossard: {player.dossard}
                        </Typography>
                      )}
                    </Box>
                  }
                />
                {index < players.length - 1 && (
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ mx: 2 }}
                  >
                    vs
                  </Typography>
                )}
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Informations supplémentaires */}
        {(match as any).createdAt && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Informations temporelles
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Créé le:{" "}
              {new Date((match as any).createdAt.toDate()).toLocaleString()}
            </Typography>
            {(match as any).updatedAt && (
              <Typography variant="body2" color="text.secondary">
                Modifié le:{" "}
                {new Date((match as any).updatedAt.toDate()).toLocaleString()}
              </Typography>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MatchDetail;
